/**
 * Authentication Service
 *
 * Admin authentication on top of Supabase Auth (GoTrue). Supabase owns
 * credentials, sessions, refresh, and password-reset email. `admin_users` is a
 * profile/role table linked to auth.users via `auth_user_id`; database RLS
 * (see migrations/000_baseline_production_schema.sql) enforces all access.
 * `AdminUser.id` is the
 * `admin_users.id` (the RBAC tables key on it), NOT the auth user id.
 */

import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { telemetry } from "@/utils/monitoring/telemetry";

export interface AdminUser {
  id: string; // admin_users.id — RBAC RPCs (get_user_roles, ...) key on this
  email: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
  lastLoginAt?: string;
  loginCount: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  user?: AdminUser;
  message: string;
}

interface AdminProfileRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_super_admin: boolean;
  last_login_at: string | null;
  login_count: number | null;
}

class AuthService {
  /**
   * Load the active admin profile for a Supabase Auth user id.
   * Returns null when there is no active admin profile (i.e. authenticated to
   * Supabase but not authorized for the admin app).
   */
  async loadProfile(authUserId: string): Promise<AdminUser | null> {
    const { data, error } = await supabase
      .from("admin_users")
      .select(
        "id, email, first_name, last_name, is_super_admin, last_login_at, login_count",
      )
      .eq("auth_user_id", authUserId)
      .eq("is_active", true)
      .maybeSingle();

    if (error) {
      logger.error("Failed to load admin profile", { error });
      return null;
    }
    if (!data) return null;

    const row = data as AdminProfileRow;
    return {
      id: row.id,
      email: row.email,
      firstName: row.first_name,
      lastName: row.last_name,
      isSuperAdmin: row.is_super_admin,
      lastLoginAt: row.last_login_at ?? undefined,
      loginCount: row.login_count ?? 0,
    };
  }

  /**
   * Log in via Supabase Auth, then resolve the admin profile.
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email.toLowerCase(),
        password: credentials.password,
      });

      if (error || !data.user) {
        telemetry.track("admin_login_failed", { email: credentials.email });
        return { success: false, message: "Invalid email or password" };
      }

      const user = await this.loadProfile(data.user.id);
      if (!user) {
        // Valid Supabase credentials but no active admin profile → deny + sign out.
        await supabase.auth.signOut();
        return {
          success: false,
          message: "This account is not authorized for admin access.",
        };
      }

      await this.logActivity({
        userId: user.id,
        action: "login",
        description: "Admin user logged in successfully",
        success: true,
      });
      telemetry.track("admin_login_success", {
        userId: user.id,
        isSuperAdmin: user.isSuperAdmin,
      });
      logger.info("Admin login successful", { userId: user.id });

      return { success: true, user, message: "Login successful" };
    } catch (error) {
      logger.error("Login error", { error });
      return { success: false, message: "Login failed. Please try again." };
    }
  }

  /**
   * Log out: record the activity (still authorized) then end the Supabase session.
   */
  async logout(): Promise<void> {
    try {
      const user = await this.getCurrentUser();
      if (user) {
        await this.logActivity({
          userId: user.id,
          action: "logout",
          description: "Admin user logged out",
          success: true,
        });
        telemetry.track("admin_logout", { userId: user.id });
      }
    } catch (error) {
      logger.error("Logout error", { error });
    } finally {
      await supabase.auth.signOut();
    }
  }

  /**
   * Resolve the current admin profile from the active Supabase session, or null.
   */
  async getCurrentUser(): Promise<AdminUser | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) return null;
    return this.loadProfile(data.session.user.id);
  }

  async isAuthenticated(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  }

  async isSuperAdmin(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user?.isSuperAdmin ?? false;
  }

  /**
   * Subscribe to auth state changes. The callback receives the resolved admin
   * profile (or null). Returns an unsubscribe function.
   */
  onAuthStateChange(callback: (user: AdminUser | null) => void): () => void {
    const { data } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, session: Session | null) => {
        // Defer the async profile load OUT of the GoTrue callback — awaiting a
        // supabase call inside the handler can deadlock the auth client.
        setTimeout(() => {
          if (!session) {
            callback(null);
            return;
          }
          void this.loadProfile(session.user.id).then(callback);
        }, 0);
      },
    );
    return () => data.subscription.unsubscribe();
  }

  /**
   * Change the current user's password (verifies the current one first).
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const email = sessionData.session?.user.email;
      if (!email) {
        return { success: false, message: "Not authenticated" };
      }

      // Verify the current password by re-authenticating.
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) {
        return { success: false, message: "Current password is incorrect" };
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) {
        logger.error("Failed to update password", { error });
        return { success: false, message: "Failed to update password" };
      }

      const user = await this.getCurrentUser();
      if (user) {
        await this.logActivity({
          userId: user.id,
          action: "password_changed",
          description: "User changed password",
          success: true,
        });
      }
      return { success: true, message: "Password changed successfully" };
    } catch (error) {
      logger.error("Error changing password", { error });
      return { success: false, message: "Failed to change password" };
    }
  }

  /**
   * Insert an audit entry. Runs as the authenticated admin (RLS enforced).
   */
  async logActivity(activity: {
    userId: string | null;
    action: string;
    description: string;
    resourceType?: string;
    resourceId?: string;
    success: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    try {
      const { error } = await supabase.from("admin_activity_log").insert({
        admin_user_id: activity.userId,
        action: activity.action,
        description: activity.description,
        resource_type: activity.resourceType || null,
        resource_id: activity.resourceId || null,
        success: activity.success,
        metadata: activity.metadata || {},
        ip_address: null,
        user_agent: navigator.userAgent,
      });

      if (error) {
        logger.error("Failed to log activity to database", { error, activity });
      }
    } catch (error) {
      logger.error("Error logging admin activity", { error, activity });
    }
  }

  /**
   * Fetch recent admin activities (admin-gated by RLS).
   */
  async getAdminActivities(limit = 100): Promise<unknown[]> {
    try {
      const { data, error } = await supabase
        .from("admin_activity_log")
        .select(
          `
          *,
          admin_users(first_name, last_name, email)
        `,
        )
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        logger.error("Error fetching admin activities", { error });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error("Error getting admin activities", { error });
      return [];
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
